import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Check, Sparkles, ArrowLeft, ArrowRight, Tag, RotateCcw, Clock } from 'lucide-react';
import {
  getServicesList,
  getCategoriesList,
  subscribeToCatalogUpdates,
  syncAllCatalogFromRemote
} from '../services/catalogService';
import { getServiceSubCategory, ServiceItem, formatDuration } from '../data/services';
import { navigateTo } from '../utils/router';

interface ServiceMenuProps {
  selectedServices: string[];
  onToggleService: (id: string, name: string, price: number) => void;
  initialCategory?: string | null;
  onSelectCategory?: (categoryId: string) => void;
  onBackToCategories?: () => void;
}

export const ServiceMenu: React.FC<ServiceMenuProps> = ({
  selectedServices,
  onToggleService,
  initialCategory = null,
  onSelectCategory,
  onBackToCategories
}) => {
  // null = show category grid, string = show that category's services
  const [activeCategory, setActiveCategory] = useState<string | null>(initialCategory);
  const [activeSubCategory, setActiveSubCategory] = useState<string>('all');
  const [servicesList, setServicesList] = useState(getServicesList);
  const [categoriesList, setCategoriesList] = useState(getCategoriesList);

  useEffect(() => {
    // Initial fetch from Cloudflare D1 database to ensure multi-system sync
    syncAllCatalogFromRemote().catch(() => {});

    const unsubscribe = subscribeToCatalogUpdates(() => {
      setServicesList(getServicesList());
      setCategoriesList(getCategoriesList());
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    setActiveCategory(initialCategory || null);
    setActiveSubCategory('all');
  }, [initialCategory]);

  useEffect(() => {
    if (activeCategory) {
      const menuEl = document.getElementById('menu');
      if (menuEl) {
        const navHeight = 75;
        const rect = menuEl.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetY = rect.top + scrollTop - navHeight;
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
      }
    }
  }, [activeCategory]);

  const handleCategoryClick = (catId: string) => {
    setActiveCategory(catId);
    setActiveSubCategory('all');
    if (onSelectCategory) {
      onSelectCategory(catId);
    }
  };

  const handleBackToAll = () => {
    setActiveCategory(null);
    setActiveSubCategory('all');
    if (onBackToCategories) {
      onBackToCategories();
    }
    const menuEl = document.getElementById('menu');
    if (menuEl) {
      const navHeight = 75;
      const rect = menuEl.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetY = rect.top + scrollTop - navHeight;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    }
  };

  const getCategoryDisplayName = (catId: string) => {
    const found = categoriesList.find(c => c.id === catId);
    if (found) return found.label;
    return catId.replace(/[-_]/g, ' ');
  };

  const activeCatDetails = activeCategory
    ? categoriesList.find(c => c.id === activeCategory)
    : null;

  const categoryGrid = categoriesList.filter(
    c => c.id !== 'all' && c.id !== 'bundles' && servicesList.some(s => s.category.toLowerCase() === c.id.toLowerCase())
  );

  // Raw services for the active category
  const rawCategoryServices = useMemo(() => {
    if (!activeCategory) return [];
    return servicesList
      .filter(item => item.category.toLowerCase() === activeCategory.toLowerCase())
      .sort((a, b) => {
        if (activeCategory === 'facial') {
          return (a.price || 0) - (b.price || 0);
        }
        const orderA = a.sortOrder !== undefined ? a.sortOrder : 99;
        const orderB = b.sortOrder !== undefined ? b.sortOrder : 99;
        if (orderA !== orderB) return orderA - orderB;
        return (a.price || 0) - (b.price || 0);
      });
  }, [servicesList, activeCategory]);

  // Exact subcategories for Waxing in required order: Hands, Underarms, Legs, Back & Stomach, Combo, Full Body
  const WAXING_SUBCATEGORIES_ORDER = ['Hands', 'Underarms', 'Legs', 'Back & Stomach', 'Combo', 'Full Body'] as const;

  // Extract unique subcategories (only for Waxing; empty for Cleanup and other categories)
  const subcategoryList = useMemo(() => {
    if (activeCategory !== 'waxing' || rawCategoryServices.length === 0) return [];
    const map = new Map<string, number>();
    rawCategoryServices.forEach(s => {
      const sub = getServiceSubCategory(s);
      if (sub) {
        map.set(sub, (map.get(sub) || 0) + 1);
      }
    });

    return WAXING_SUBCATEGORIES_ORDER
      .filter(name => map.has(name))
      .map(name => ({ name, count: map.get(name)! }));
  }, [activeCategory, rawCategoryServices]);

  // Services matching active subcategory
  const filteredServices = useMemo(() => {
    if (activeCategory !== 'waxing' || activeSubCategory === 'all') {
      return rawCategoryServices;
    }
    return rawCategoryServices.filter(s => getServiceSubCategory(s) === activeSubCategory);
  }, [rawCategoryServices, activeSubCategory, activeCategory]);

  // Compute discount % badge
  const discountPct = (price: number, orig: number) =>
    orig > 0 ? Math.round(((orig - price) / orig) * 100) : 0;

  const renderServiceCard = (service: ServiceItem) => {
    const isSelected = selectedServices.includes(service.id);
    const pct = discountPct(service.price, service.originalPrice);
    const subCat = activeCategory === 'waxing' ? getServiceSubCategory(service) : null;

    return (
      <div key={service.id} className="service-item-card minimal-card">
        <div className="service-card-top">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 className="service-title">{service.name}</h4>
            <div className="service-card-meta-row" style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
              <span className="service-duration-badge" title="Estimated service duration">
                <Clock size={11} style={{ marginRight: '3px', verticalAlign: 'middle' }} />
                {formatDuration(service.durationMinutes)}
              </span>
              {service.description && (
                <span className="service-mini-tag">{service.description}</span>
              )}
            </div>
          </div>

          {/* Price block: strikethrough + discounted */}
          <div className="service-price-block">
            <span className="service-price-original">₹{service.originalPrice.toLocaleString('en-IN')}</span>
            <span className="service-price-tag">
              {service.displayPrice ? service.displayPrice : `₹${service.price}`}
            </span>
            {pct > 0 && (
              <span className="service-discount-badge">
                <Tag size={10} />
                {pct}% off
              </span>
            )}
          </div>
        </div>

        <div className="service-card-action" style={{ paddingTop: '0.6rem', marginTop: '0.5rem' }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>
            {subCat || getCategoryDisplayName(service.category)}
          </span>
          <button
            type="button"
            onClick={() => onToggleService(service.id, service.name, service.price)}
            className={`btn ${isSelected ? 'btn-berry' : 'btn-outline'} btn-sm`}
            style={{ padding: '0.35rem 0.9rem', fontSize: '0.82rem' }}
          >
            {isSelected ? (
              <>
                <Check size={13} />
                <span>Added</span>
              </>
            ) : (
              <>
                <Plus size={13} />
                <span>Add</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <section id="menu" className={`menu-section ${activeCategory ? 'category-active' : ''}`}>
      <div className="section-container">
        {/* Section Header */}
        <div className={`section-title-wrap ${activeCategory ? 'cat-active-hide-mobile' : ''}`}>
          <h2 className="section-main-heading">Services Offered</h2>
          <span className="section-script-subtitle">Salon quality beauty, at your home ♡</span>
        </div>

        {/* ── CATEGORY GRID VIEW ── */}
        {activeCategory === null && (
          <div className="cat-grid-2x3">
            {/* Featured Bundles & Combos Card */}
            <div
              className="cat-grid-card cat-grid-card-featured"
              onClick={() => {
                navigateTo('/bundles');
              }}
              role="button"
              tabIndex={0}
              aria-label="View Curated Beauty Bundles & Combos"
              onKeyDown={e => e.key === 'Enter' && navigateTo('/bundles')}
            >
              <div className="cat-grid-img-wrap">
                <img
                  src="/category-bundles.jpg"
                  alt="Curated Beauty Bundles & Packages in Bhilai & Durg"
                  loading="lazy"
                  className="cat-grid-img"
                />
                <span className="cat-featured-badge">
                  <Sparkles size={11} />
                  <span>Save Up To 25%</span>
                </span>
              </div>
              <div className="cat-grid-footer">
                <div className="cat-grid-footer-text">
                  <h3 className="cat-grid-label">Special Bundles</h3>
                  <span className="cat-grid-subtext">All-in-one head-to-toe parlour combos</span>
                </div>
                <div className="cat-grid-arrow-btn" aria-hidden="true">
                  <ArrowRight size={16} />
                </div>
              </div>
            </div>

            {categoryGrid.map(cat => (
              <div
                key={cat.id}
                className="cat-grid-card"
                onClick={() => handleCategoryClick(cat.id)}
                role="button"
                tabIndex={0}
                aria-label={`View ${cat.label} services`}
                onKeyDown={e => e.key === 'Enter' && handleCategoryClick(cat.id)}
              >
                {/* 100% visible image or graceful fallback */}
                <div className="cat-grid-img-wrap">
                  {cat.image ? (
                    <img
                      src={cat.image}
                      alt={`${cat.label} - Doorstep parlour service in Bhilai & Durg`}
                      loading="lazy"
                      className="cat-grid-img"
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        minHeight: '200px',
                        background: 'linear-gradient(135deg, #fdf2f4 0%, #fbe8ec 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        color: 'var(--blishka-berry)'
                      }}
                    >
                      <Sparkles size={38} color="var(--blishka-berry)" style={{ opacity: 0.85 }} />
                      <span style={{ fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.02em' }}>{cat.label}</span>
                    </div>
                  )}
                </div>

                {/* Clean white footer with title and arrow */}
                <div className="cat-grid-footer">
                  <div className="cat-grid-footer-text">
                    <h3 className="cat-grid-label">{cat.label}</h3>
                    <span className="cat-grid-subtext">{cat.tagline}</span>
                  </div>
                  <div className="cat-grid-arrow-btn" aria-hidden="true">
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── SELECTED CATEGORY VIEW ── */}
        {activeCategory !== null && (
          <div className="cat-detail-layout">
            {/* Creative Category Spotlight Panel */}
            <aside className="cat-spotlight-panel">
              <button
                className="cat-back-btn"
                onClick={handleBackToAll}
              >
                <ArrowLeft size={15} />
                <span>All Categories</span>
              </button>

              {activeCatDetails && (
                <div className="cat-spotlight-card">
                  {activeCatDetails.image ? (
                    <div className="cat-spotlight-frame">
                      <img
                        src={activeCatDetails.image}
                        alt={activeCatDetails.label}
                        className="cat-spotlight-img"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div
                      className="cat-spotlight-fallback"
                      style={{
                        width: '100%',
                        height: '160px',
                        background: 'linear-gradient(135deg, #fdf2f4 0%, #fbe8ec 100%)',
                        borderRadius: '16px 16px 0 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        color: 'var(--blishka-berry)'
                      }}
                    >
                      <Sparkles size={36} color="var(--blishka-berry)" />
                      <span style={{ fontSize: '1rem', fontWeight: 700 }}>{activeCatDetails.label}</span>
                    </div>
                  )}

                  <div className="cat-spotlight-body">
                    <span className="cat-spotlight-pill">
                      <Sparkles size={12} />
                      <span>{activeCategory === 'bundles' ? 'Curated Combos' : 'Doorstep Care'}</span>
                    </span>
                    <h3 className="cat-spotlight-title" style={{ paddingLeft: '6px', paddingTop: '4px', overflow: 'visible' }}>
                      {activeCatDetails.label}
                    </h3>
                    <p className="cat-spotlight-desc">{activeCatDetails.tagline}</p>

                    <div className="cat-spotlight-perks">
                      <div className="cat-perk-item">
                        <Check size={13} className="cat-perk-icon" />
                        <span>100% Single-use Disposables</span>
                      </div>
                      <div className="cat-perk-item">
                        <Check size={13} className="cat-perk-icon" />
                        <span>Salon-grade Premium Care</span>
                      </div>
                      <div className="cat-perk-item">
                        <Check size={13} className="cat-perk-icon" />
                        <span>Certified Expert Beauticians</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </aside>

            {/* Services Grid Column */}
            <div className="cat-services-column">
              <div className="cat-services-heading-row">
                <div>
                  <h3 className="cat-services-title">{activeCatDetails?.label} Menu</h3>
                  <span className="cat-services-subtitle">
                    {filteredServices.length} {filteredServices.length === 1 ? 'service' : 'services'} available at exclusive doorstep rates
                  </span>
                </div>
              </div>

              {/* ── SUBCATEGORY FILTER BAR ── */}
              {subcategoryList.length > 1 && (
                <div className="subcategory-filter-wrapper" role="navigation" aria-label="Filter subcategories">
                  {/* Subcategory Pills */}
                  <div className="subcategory-pill-row">
                    <button
                      type="button"
                      className={`subcategory-pill ${activeSubCategory === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveSubCategory('all')}
                      aria-pressed={activeSubCategory === 'all'}
                    >
                      <span>All</span>
                      <span className="subcategory-pill-badge">{rawCategoryServices.length}</span>
                    </button>
                    {subcategoryList.map(({ name, count }) => (
                      <button
                        key={name}
                        type="button"
                        className={`subcategory-pill ${activeSubCategory === name ? 'active' : ''}`}
                        onClick={() => setActiveSubCategory(name)}
                        aria-pressed={activeSubCategory === name}
                      >
                        <span>{name}</span>
                        <span className="subcategory-pill-badge">{count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── ACTIVE FILTER NOTICE (if specific pill applied) ── */}
              {activeCategory === 'waxing' && activeSubCategory !== 'all' && (
                <div className="active-filter-banner">
                  <span className="active-filter-banner-text">
                    Filtered by: <strong>{activeSubCategory}</strong> ({filteredServices.length} {filteredServices.length === 1 ? 'service' : 'services'})
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveSubCategory('all')}
                    className="active-filter-reset-btn"
                  >
                    View All Waxing
                  </button>
                </div>
              )}

              {/* ── SERVICES VIEW: SEGREGATED SECTIONS OR FILTERED GRID ── */}
              {filteredServices.length === 0 ? (
                <div className="no-filter-results">
                  <p>No services match the selected filter.</p>
                  <button
                    type="button"
                    onClick={() => setActiveSubCategory('all')}
                    className="btn btn-berry btn-sm"
                    style={{ marginTop: '0.75rem' }}
                  >
                    <RotateCcw size={13} style={{ marginRight: '0.35rem' }} />
                    Reset Filter
                  </button>
                </div>
              ) : activeCategory === 'waxing' && activeSubCategory === 'all' && subcategoryList.length > 1 ? (
                /* ── SEGREGATED BY WAXING SUBCATEGORY HEADINGS ── */
                <div className="subcategory-sections-container">
                  {subcategoryList.map(({ name }) => {
                    const sectionServices = filteredServices.filter(s => getServiceSubCategory(s) === name);
                    if (sectionServices.length === 0) return null;

                    return (
                      <div key={name} className="subcategory-section-block">
                        <div className="subcategory-section-header">
                          <div className="subcategory-section-title-wrap">
                            <h4 className="subcategory-section-title">{name}</h4>
                            <span className="subcategory-section-count">
                              {sectionServices.length} {sectionServices.length === 1 ? 'service' : 'services'}
                            </span>
                          </div>
                        </div>

                        <div className="services-grid">
                          {sectionServices.map(renderServiceCard)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* ── DIRECT GRID (Specific subcategory or other categories like Cleanup) ── */
                <div className="services-grid">
                  {filteredServices.map(renderServiceCard)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
